/**
 * The refusals the AI Provider endpoints can answer with, one class each.
 *
 * Mirrors `llm-profiles/errors.ts`: each refusal is a different thing for the
 * person to do, so a typed class per refusal rather than a bare `code` string
 * a component would have to narrow itself (constitution §2.5 forbids
 * narrowing `unknown` without a type guard).
 */

export type AiProviderErrorField =
  'name' | 'provider_type' | 'base_url' | 'token' | 'form';

class AiProviderError extends Error {
  readonly status: number;
  readonly field: AiProviderErrorField;

  constructor(
    name: string,
    status: number,
    field: AiProviderErrorField,
    message: string,
  ) {
    super(message);
    this.name = name;
    this.field = field;
    this.status = status;
  }
}

/** The name is empty after trimming, or over length. */
export class AiProviderNameInvalidError extends AiProviderError {
  constructor(message?: string) {
    super(
      'AiProviderNameInvalidError',
      422,
      'name',
      message ?? 'Enter a name.',
    );
  }
}

/** `provider_type` is not `anthropic` — every other member is not yet creatable (FR-004). */
export class AiProviderTypeInvalidError extends AiProviderError {
  constructor(message?: string) {
    super(
      'AiProviderTypeInvalidError',
      422,
      'provider_type',
      message ?? 'Only Anthropic can be created today.',
    );
  }
}

/** `base_url` is non-blank but not a well-formed URL. */
export class AiProviderBaseUrlInvalidError extends AiProviderError {
  constructor(message?: string) {
    super(
      'AiProviderBaseUrlInvalidError',
      422,
      'base_url',
      message ?? 'Enter a well-formed URL, or leave it blank.',
    );
  }
}

/** A token was required (create) but missing or blank. */
export class AiProviderTokenRequiredError extends AiProviderError {
  constructor(message?: string) {
    super(
      'AiProviderTokenRequiredError',
      422,
      'token',
      message ?? 'Enter a token.',
    );
  }
}

/** The provider is gone — deleted from another tab, or by someone else. */
export class AiProviderNotFoundError extends AiProviderError {
  constructor(message?: string) {
    super(
      'AiProviderNotFoundError',
      404,
      'form',
      message ??
        'That AI Provider no longer exists. The list has been refreshed.',
    );
  }
}

/**
 * At least one LLM Profile still references this provider (spec FR-009).
 *
 * Form-level and named explicitly — the admin needs to know *why* the delete
 * was refused, not just that it was.
 */
export class AiProviderHasDependentProfilesError extends AiProviderError {
  constructor(message?: string) {
    super(
      'AiProviderHasDependentProfilesError',
      409,
      'form',
      message ??
        'This provider is still referenced by at least one LLM Profile, so it cannot be deleted. Reassign or delete those profiles first.',
    );
  }
}

/** This provider is the current default (spec FR-009b). */
export class AiProviderIsDefaultError extends AiProviderError {
  constructor(message?: string) {
    super(
      'AiProviderIsDefaultError',
      409,
      'form',
      message ??
        'This is the default AI Provider, so it cannot be deleted. Set a different provider as default first.',
    );
  }
}

/** A refusal this build does not recognise. Form-level and deliberately vague. */
export class AiProviderRequestFailedError extends AiProviderError {
  constructor(status: number, message?: string) {
    super(
      'AiProviderRequestFailedError',
      status,
      'form',
      message ?? 'The request failed. Please try again.',
    );
  }
}

const BY_CODE: Record<string, (message?: string) => AiProviderError> = {
  AI_PROVIDER_NAME_INVALID: (m) => new AiProviderNameInvalidError(m),
  AI_PROVIDER_TYPE_INVALID: (m) => new AiProviderTypeInvalidError(m),
  AI_PROVIDER_BASE_URL_INVALID: (m) => new AiProviderBaseUrlInvalidError(m),
  AI_PROVIDER_TOKEN_REQUIRED: (m) => new AiProviderTokenRequiredError(m),
  AI_PROVIDER_NOT_FOUND: (m) => new AiProviderNotFoundError(m),
  provider_has_dependent_profiles: (m) =>
    new AiProviderHasDependentProfilesError(m),
  AI_PROVIDER_HAS_DEPENDENT_PROFILES: (m) =>
    new AiProviderHasDependentProfilesError(m),
  provider_is_default: (m) => new AiProviderIsDefaultError(m),
  AI_PROVIDER_IS_DEFAULT: (m) => new AiProviderIsDefaultError(m),
};

/**
 * Map one error response to its typed refusal.
 *
 * The service's own `message` is deliberately **not** passed through — see
 * `llm-profiles/errors.ts`'s identical note. An unrecognised code produces
 * {@link AiProviderRequestFailedError} instead of throwing.
 */
export function toAiProviderError(
  status: number,
  body: unknown,
): AiProviderError {
  const code =
    typeof body === 'object' &&
    body !== null &&
    ('error' in body || 'code' in body)
      ? ((body as Record<string, unknown>).error ??
        (body as Record<string, unknown>).code)
      : undefined;

  const construct = typeof code === 'string' ? BY_CODE[code] : undefined;
  return construct ? construct() : new AiProviderRequestFailedError(status);
}

/** Narrows a mutation's `unknown` error to a typed refusal (constitution §2.5). */
export function isAiProviderError(error: unknown): error is AiProviderError {
  return error instanceof AiProviderError;
}

export type { AiProviderError };
